import { supabase } from '@/integrations/supabase/client';

export type MarsPriceTier = 'pesin' | '30' | '60' | '90' | '120' | '150' | '180';

export interface MarsPriceData {
  pesin_price: string;
  price_30: string;
  price_60: string;
  price_90: string;
  price_120: string;
  price_150: string;
  price_180: string;
  price_date: string;
  last_updated_at: string;
}

/**
 * Get Mars price for a product based on SKU, sale date, and price tier
 * @param sku Product SKU (item_code)
 * @param saleDate Date of the sale (to match with price_date)
 * @param tier Price tier to use (pesin, 30, 60, 90, 120, 150, 180)
 * @param sameMonthOnly If true, only get price from the same month as saleDate
 * @returns The price as a number, or null if not found
 */
export async function getMarsPrice(
  sku: string | null | undefined,
  saleDate: Date | string,
  tier: MarsPriceTier = 'pesin',
  sameMonthOnly: boolean = false
): Promise<number | null> {
  if (!sku) return null;

  try {
    // Convert saleDate to Date if it's a string
    const saleDateObj = typeof saleDate === 'string' ? new Date(saleDate) : saleDate;
    const saleDateStr = saleDateObj.toISOString().split('T')[0]; // YYYY-MM-DD format

    let query = supabase
      .from('mars_product_prices')
      .select('*')
      .eq('sku', sku)
      .lte('price_date', saleDateStr)
      .order('price_date', { ascending: false });

    // If sameMonthOnly is true, filter to same month
    if (sameMonthOnly) {
      const saleYear = saleDateObj.getFullYear();
      const saleMonth = saleDateObj.getMonth();
      const firstDayOfMonth = new Date(saleYear, saleMonth, 1);
      const lastDayOfMonth = new Date(saleYear, saleMonth + 1, 0);
      
      query = query
        .gte('price_date', firstDayOfMonth.toISOString().split('T')[0])
        .lte('price_date', lastDayOfMonth.toISOString().split('T')[0]);
    }

    const { data, error } = await query.limit(1).maybeSingle();

    if (error) {
      console.error('Error fetching Mars price:', error);
      return null;
    }

    if (!data) {
      return null;
    }

    // Get the price based on tier
    const priceMap: Record<MarsPriceTier, keyof MarsPriceData> = {
      pesin: 'pesin_price',
      '30': 'price_30',
      '60': 'price_60',
      '90': 'price_90',
      '120': 'price_120',
      '150': 'price_150',
      '180': 'price_180',
    };

    const priceKey = priceMap[tier];
    const price = data[priceKey];

    if (!price) {
      return null;
    }

    return parseFloat(price.toString());
  } catch (error) {
    console.error('Error in getMarsPrice:', error);
    return null;
  }
}

/**
 * Get Mars price for multiple products in batch
 * This is more efficient when calculating profit for many sales
 * @param items Array of { sku, saleDate, tier }
 * @param sameMonthOnly If true, only get prices from the same month as saleDate
 * @returns Map of SKU -> price
 */
export async function getMarsPricesBatch(
  items: Array<{ sku: string | null | undefined; saleDate: Date | string; tier?: MarsPriceTier }>,
  sameMonthOnly: boolean = false
): Promise<Map<string, number>> {
  const priceMap = new Map<string, number>();
  const tier = items[0]?.tier || 'pesin';

  // Group by month to optimize queries when sameMonthOnly is true
  const monthGroups = new Map<string, Array<{ sku: string; saleDate: Date }>>();
  
  items.forEach((item) => {
    if (!item.sku) return;
    
    const saleDateObj = typeof item.saleDate === 'string' ? new Date(item.saleDate) : item.saleDate;
    
    if (sameMonthOnly) {
      const monthKey = `${saleDateObj.getFullYear()}-${saleDateObj.getMonth()}`;
      if (!monthGroups.has(monthKey)) {
        monthGroups.set(monthKey, []);
      }
      monthGroups.get(monthKey)!.push({ sku: item.sku, saleDate: saleDateObj });
    } else {
      // Group by date for non-same-month queries
      const dateStr = saleDateObj.toISOString().split('T')[0];
      const monthKey = `date-${dateStr}`;
      if (!monthGroups.has(monthKey)) {
        monthGroups.set(monthKey, []);
      }
      monthGroups.get(monthKey)!.push({ sku: item.sku, saleDate: saleDateObj });
    }
  });

  // Fetch prices for each month/date group
  const pricePromises = Array.from(monthGroups.entries()).map(async ([monthKey, skuItems]) => {
    const skus = skuItems.map(item => item.sku);
    const firstItem = skuItems[0];
    const saleDateObj = firstItem.saleDate;
    const saleDateStr = saleDateObj.toISOString().split('T')[0];

    let query = supabase
      .from('mars_product_prices')
      .select('*')
      .in('sku', skus)
      .lte('price_date', saleDateStr)
      .order('price_date', { ascending: false });

    // If sameMonthOnly, filter to same month
    if (sameMonthOnly) {
      const saleYear = saleDateObj.getFullYear();
      const saleMonth = saleDateObj.getMonth();
      const firstDayOfMonth = new Date(saleYear, saleMonth, 1);
      const lastDayOfMonth = new Date(saleYear, saleMonth + 1, 0);
      
      query = query
        .gte('price_date', firstDayOfMonth.toISOString().split('T')[0])
        .lte('price_date', lastDayOfMonth.toISOString().split('T')[0]);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching Mars prices batch:', error);
      return [];
    }

    // Group by SKU and get the latest price for each
    const skuPriceMap = new Map<string, any>();
    (data || []).forEach((priceRecord) => {
      const existing = skuPriceMap.get(priceRecord.sku);
      if (!existing || new Date(priceRecord.price_date) > new Date(existing.price_date)) {
        skuPriceMap.set(priceRecord.sku, priceRecord);
      }
    });

    return Array.from(skuPriceMap.entries()).map(([sku, priceRecord]) => {
      const priceMap: Record<MarsPriceTier, keyof MarsPriceData> = {
        pesin: 'pesin_price',
        '30': 'price_30',
        '60': 'price_60',
        '90': 'price_90',
        '120': 'price_120',
        '150': 'price_150',
        '180': 'price_180',
      };

      const priceKey = priceMap[tier];
      const price = priceRecord[priceKey];
      return { sku, price: price ? parseFloat(price.toString()) : null };
    });
  });

  const results = await Promise.all(pricePromises);
  results.flat().forEach(({ sku, price }) => {
    if (price !== null) {
      priceMap.set(sku, price);
    }
  });

  return priceMap;
}
